import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';

import { LanguageCode, LanguageService } from '../../app/shared/services/language.service';

/**
 * Header language picker: an icon trigger that opens a small dropdown of the
 * available UI languages (English / Deutsch), mirroring the user-profile menu's
 * open/close + outside-click/Escape behaviour. Selecting a language delegates to
 * {@link LanguageService.setLanguage}, which swaps the active `@ngx-translate`
 * language so every `| translate` binding re-renders.
 *
 * Presentational + self-contained: it owns only the open/close state; the actual
 * language state lives in the root {@link LanguageService}.
 */
@Component({
  selector: 'app-language-menu',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule],
  templateUrl: './language-menu.component.html',
  styleUrl: './language-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageMenuComponent {
  private readonly languageService = inject(LanguageService);
  /** Host element — used for outside-click detection. */
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * When `false`, the picker is inert — the trigger is disabled and the dropdown
   * cannot open. Defaults to `true`. Set `[enabled]="false"` to switch the language
   * menu off (e.g. while translation is not yet ready for a mount).
   */
  readonly enabled = input(true);

  /** Languages offered in the dropdown. */
  readonly languages = this.languageService.languages;
  /** The active language code (drives the check mark / active row). */
  readonly current = this.languageService.current;
  /** Whether the dropdown panel is open. */
  readonly isOpen = signal(false);

  /** Toggles the dropdown open/closed. No-op while disabled. */
  toggle(): void {
    if (!this.enabled()) {
      return;
    }
    this.isOpen.update(open => !open);
  }

  /** Closes the dropdown. */
  close(): void {
    this.isOpen.set(false);
  }

  /**
   * Applies the chosen language and closes the dropdown.
   * @param code The selected language code.
   */
  choose(code: LanguageCode): void {
    this.languageService.setLanguage(code);
    this.close();
  }

  /** Closes the dropdown when a click lands outside this component. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen() && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  /** Closes the dropdown on Escape. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }
}
